import { NgClass } from "@angular/common";
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CreateDeckRequest, CreateDeckResponse, GameType, VisibilityType } from "@apiserver-rest/ponder-data-client-base";
import { DeckTypes } from "@ponder-shared/utils/deckTypes";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { VisibilityTypesReversed } from "@ponder-shared/utils/visbilityTypes";
import { Subscription, take } from "rxjs";
import { getDeckRoute } from "../../deck-router";
import { DataBroker } from "@ponder-core/data/dataBroker";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "deck-create-modal",
    imports: [NgClass, BusySpinComponent, ReactiveFormsModule],
    templateUrl: "./deck-create-modal.component.html"
})
export class DeckCreateModalComponent implements AfterViewInit {
    @ViewChild("createModalContainer") private createModalContainerRef!: ElementRef<HTMLElement>;
    
    public isCreating: boolean = false;
    public creationFailed: boolean = false;
    public errorMessage?: string;

    public isVisible: boolean = false;

    public createDeckForm!: FormGroup;
    public currColor: string = "#292837";
    public prevColor: string | undefined;
    public deckTypes: string[] = DeckTypes.get(GameType.Magic)!;
    public deckGroups: string[] = [];

    public readonly visibilityTypes = VisibilityTypesReversed;
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly undoSvgIcon: string = SvgIconMap.get(SvgIcon.Undo)!;

    private showModalSub!: Subscription;

    public constructor(formBuilder: FormBuilder, private ponderDataClient: PonderDataClient, private data: DataBroker, private events: EventBroker, private navigation: NavigationService) {
        this.createDeckForm = formBuilder.group({
            name: ["", [Validators.required, Validators.minLength(3)]],
            group: ["Default", [Validators.required, Validators.minLength(3)]],
            game: [GameType.Magic, Validators.required],
            type: ["", Validators.required],
            visibility: [VisibilityType.Private, Validators.required],
            color: [this.currColor, [Validators.required, Validators.minLength(7), Validators.maxLength(7)]],
        });
        
        this.showModalSub = this.events.deck.showDeckCreateModalEvent.subscribe({
            next: () => {
                this.onOpen();
            }
        });
    }

    public ngOnDestroy(): void {
        this.showModalSub.unsubscribe();
    }

    public ngAfterViewInit(): void {
        this.data.deck.requestDeckGroups.emit((groups: string[]) => {
            this.deckGroups = groups;
        });
    }

    public onOpen(): void {
        this.isVisible = true;
    }

    public onClose(): void {
        if (!this.isCreating) {
            this.creationFailed = false;
            this.isVisible = false;
        }

        this.createDeckForm.patchValue({ name: "" });
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.isVisible && !this.createModalContainerRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.isVisible && this.mouseDownOutside && !this.createModalContainerRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }
    
    // -------------------------------------------------------------------------------------
    // On Value Change
    // -------------------------------------------------------------------------------------

    public canSubmit(): boolean {
        return this.isVisible && !this.isCreating && this.createDeckForm.valid && !this.creationFailed;
    }

    public onGameChange(value: string): void {
        this.deckTypes = DeckTypes.get(value as GameType)!;
    }

    public onColorChange(value: string): void {
        this.prevColor = this.currColor;
        this.currColor = value;
    }

    public onUndoColorClicked(): void {
        if (this.prevColor) {
            this.createDeckForm.patchValue({ color: this.prevColor });
            this.currColor = this.prevColor;
            this.prevColor = undefined;
        }
    }

    // -------------------------------------------------------------------------------------
    // Create
    // -------------------------------------------------------------------------------------

    public onCreateClicked(): void {
        if (!this.canSubmit()) {
            return;
        }

        this.isCreating = true;
        this.creationFailed = false;

        var request = new CreateDeckRequest({
            name: this.createDeckForm.get("name")!.value,
            game: this.createDeckForm.get("game")!.value,
            type: this.createDeckForm.get("type")!.value,
            visibility: this.createDeckForm.get("visibility")!.value,
            group: this.createDeckForm.get("group")!.value,
            color: this.createDeckForm.get("color")!.value
        });
        
        this.ponderDataClient
            .deckCreate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: CreateDeckResponse) => {
                    if (response.success && response.deckId && response.owner) {
                        this.navigation.goto(getDeckRoute(response.owner, response.deckId));
                    } else {
                        this.errorMessage = response.error;
                        this.creationFailed = true;
                    }
                },
                error: () => {
                    this.creationFailed = true;
                }
            })
            .add(() => { this.isCreating = false; })
    }
}